/**
 * Price display helpers for the plan picker. Amounts always come from Stripe (cents); nothing here
 * invents a discount: the yearly deal is derived from the two real prices.
 */
export interface YearlyDeal {
  /** Yearly price spread over 12 months, in cents. */
  perMonthCents: number;
  /** What 12 monthly payments would cost minus the yearly price, in cents (never negative). */
  savedCents: number;
  /** Rounded percentage saved against paying monthly. */
  percent: number;
  /** Whole months you do not pay for (10 × monthly = yearly → 2), `null` when it is not a whole number. */
  monthsFree: number | null;
}

export function yearlyDeal(monthlyCents: number, yearlyCents: number): YearlyDeal {
  const full = monthlyCents * 12;
  const savedCents = Math.max(0, full - yearlyCents);
  const months = monthlyCents > 0 ? savedCents / monthlyCents : 0;
  return {
    perMonthCents: Math.round(yearlyCents / 12),
    savedCents,
    percent: full > 0 ? Math.round((savedCents / full) * 100) : 0,
    monthsFree: months >= 1 && Number.isInteger(months) ? months : null,
  };
}

/** Whole euros without decimals ("€39"), otherwise two decimals ("€32,50"). */
export function formatEuro(cents: number, locale: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}
